import React from 'react'
import { Table, Spinner } from 'react-bootstrap'
import { Transaction } from '../types'
import * as S from '../styles'

interface TransactionTableProps {
  transactions: Transaction[]
  loading: boolean
}

export const TransactionTable: React.FC<TransactionTableProps> = ({
  transactions,
  loading
}) => {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date))
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      contract_registration: 'Registro de Contrato',
      receivable_registration: 'Registro de Recebível',
      token_deployment: 'Implantação de Token',
      payment_confirmation: 'Confirmação de Pagamento',
      smart_contract_deployment: 'Implantação de Smart Contract'
    }
    return types[type] || type
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      contract_registration: 'primary',
      receivable_registration: 'success',
      token_deployment: 'info',
      payment_confirmation: 'warning',
      smart_contract_deployment: 'secondary'
    }
    return colors[type] || 'light'
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Could add a toast notification here
  }

  const openBlockchainExplorer = (hash: string) => {
    // Since this is a private Besu network, we might have our own explorer
    // For now, just copy to clipboard
    copyToClipboard(hash)
  }

  if (loading) {
    return (
      <S.LoadingSpinner>
        <Spinner animation="border" />
      </S.LoadingSpinner>
    )
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Nenhuma transação encontrada</p>
      </div>
    )
  }

  return (
    <S.TableContainer>
      <Table responsive hover>
        <thead>
          <tr>
            <th>Hash da Transação</th>
            <th>Tipo</th>
            <th>Smart Contract</th>
            <th>Gas Usado</th>
            <th>Data/Hora</th>
            <th>Contrato ID</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => (
            <tr key={transaction.id}>
              <td>
                <S.TransactionHash
                  onClick={() =>
                    openBlockchainExplorer(transaction.transactionHash)
                  }
                  title="Clique para copiar o hash completo"
                >
                  {transaction.transactionHash.substring(0, 12)}...
                  {transaction.transactionHash.substring(
                    transaction.transactionHash.length - 8
                  )}
                </S.TransactionHash>
              </td>
              <td>
                <S.StatusBadge status={transaction.type}>
                  {getTypeLabel(transaction.type)}
                </S.StatusBadge>
              </td>
              <td>
                <div>
                  <strong>{transaction.smartContractType}</strong>
                  <br />
                  <small className="text-muted">
                    <S.TransactionHash
                      onClick={() =>
                        copyToClipboard(transaction.smartContractAddress)
                      }
                      title="Clique para copiar o endereço"
                    >
                      {transaction.smartContractAddress.substring(0, 8)}...
                      {transaction.smartContractAddress.substring(
                        transaction.smartContractAddress.length - 6
                      )}
                    </S.TransactionHash>
                  </small>
                </div>
              </td>
              <td>
                <span className="font-weight-bold">
                  {formatNumber(transaction.gasConsumed)}
                </span>
                <br />
                <small className="text-muted">gas</small>
              </td>
              <td>
                <span>{formatDate(transaction.createdAt)}</span>
              </td>
              <td>
                {transaction.contractId ? (
                  <code className="text-primary">{transaction.contractId}</code>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </S.TableContainer>
  )
}
